import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Trash2, Send, LogOut, Users, MessageSquare, Package, Plus, Edit2, X, Upload } from 'lucide-react';
import { playNotifySound, initAudio } from './notifySound';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPanel = () => {
  const [token, setToken] = useState(() => localStorage.getItem('admin_token') || '');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [tab, setTab] = useState('chats'); // 'chats' | 'products'
  const [chats, setChats] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [activeCount, setActiveCount] = useState({ active: 0, total_sessions: 0 });
  const [products, setProducts] = useState([]);
  const [showProducts, setShowProducts] = useState(false);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const messagesEndRef = useRef(null);
  const totalUnreadRef = useRef(0);

  // Audio unlock
  useEffect(() => { initAudio(); }, []);

  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  // Login
  const login = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem('admin_token', res.data.token);
      setToken(res.data.token);
    } catch {
      setLoginError('Şifre hatalı');
    }
  };

  const logout = () => {
    localStorage.removeItem('admin_token');
    setToken('');
    setPassword('');
  };

  // Load chats + active count polling
  useEffect(() => {
    if (!token) return;
    const load = async () => {
      try {
        const [chatsRes, countRes] = await Promise.all([
          axios.get(`${API}/admin/chats`, authHeader),
          axios.get(`${API}/admin/active-count`, authHeader),
        ]);
        const newChats = chatsRes.data.sessions || [];
        // Toplam okunmamış mesaj sayısı arttıysa ses çal (herhangi bir sohbete yeni mesaj geldi)
        const totalUnread = newChats.reduce((s, c) => s + (c.unread_by_admin || 0), 0);
        if (totalUnread > totalUnreadRef.current) {
          playNotifySound('admin');
        }
        totalUnreadRef.current = totalUnread;
        setChats(newChats);
        setActiveCount(countRes.data);
      } catch (err) {
        if (err?.response?.status === 401) logout();
      }
    };
    load();
    const interval = setInterval(load, 3000);
    return () => clearInterval(interval);
  }, [token]);

  // Load products once
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/products`).then(res => setProducts(res.data)).catch(() => {});
  }, [token]);

  // Load messages for selected chat
  useEffect(() => {
    if (!token || !selectedId) return;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/admin/messages/${selectedId}`, authHeader);
        if (!cancelled) {
          setMessages(res.data.messages || []);
          setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
        }
      } catch (e) { /* ignore */ }
    };
    load();
    const interval = setInterval(load, 3000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [token, selectedId]);

  const sendReply = async (e) => {
    e?.preventDefault();
    if (!reply.trim() || sending || !selectedId) return;
    const txt = reply.trim();
    setReply('');
    setSending(true);
    setMessages(prev => [...prev, {
      from: 'admin', text: txt, created_at: new Date().toISOString(),
    }]);
    try {
      await axios.post(`${API}/admin/reply`, { session_id: selectedId, text: txt }, authHeader);
    } catch {
      alert('Mesaj gönderilemedi');
    } finally {
      setSending(false);
    }
  };

  const deleteChat = async (id) => {
    if (!window.confirm('Bu sohbeti kalıcı olarak silmek istiyor musunuz? Karşı tarafta da silinecek.')) return;
    try {
      await axios.delete(`${API}/admin/chats/${id}`, authHeader);
      if (selectedId === id) {
        setSelectedId(null);
        setMessages([]);
      }
      setChats(prev => prev.filter(c => c.session_id !== id));
    } catch {
      alert('Silinemedi');
    }
  };

  const sendProductLink = (product) => {
    const link = `${window.location.origin}/urun/${product.id}`;
    setReply(prev => (prev ? prev + ' ' : '') + `📦 ${product.name} - ${product.price} TL: ${link}`);
    setShowProducts(false);
  };

  const sendCartLink = () => {
    const link = `${window.location.origin}/sepet`;
    setReply(prev => (prev ? prev + ' ' : '') + `🛒 Sepete git: ${link}`);
  };

  // ---- Product Management ----
  const reloadProducts = async () => {
    try {
      const res = await axios.get(`${API}/products`);
      setProducts(res.data);
    } catch {}
  };

  const deleteProduct = async (id) => {
    if (!window.confirm('Bu ürünü silmek istediğinize emin misiniz?')) return;
    try {
      await axios.delete(`${API}/admin/products/${id}`, authHeader);
      setProducts(prev => prev.filter(p => p.id !== id));
    } catch { alert('Silinemedi'); }
  };

  // Login screen
  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#faf7f2] to-white flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
          <div className="text-center mb-6">
            <div className="text-5xl mb-3">🌸</div>
            <h1 className="text-2xl font-serif font-bold text-[#2c1810]">Admin Paneli</h1>
            <p className="text-sm text-gray-500 mt-1">Hasankeyf Tesettür Yönetim</p>
          </div>
          <form onSubmit={login} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#2c1810] mb-2">Şifre</label>
              <input
                type="password"
                required
                autoFocus
                value={password}
                onChange={e => setPassword(e.target.value)}
                data-testid="admin-password-input"
                className="w-full border border-gray-200 px-4 py-3 rounded-lg focus:border-[#1a1a1a] focus:outline-none"
              />
            </div>
            {loginError && <p className="text-red-500 text-sm">{loginError}</p>}
            <button
              type="submit"
              data-testid="admin-login-button"
              className="w-full bg-[#1a1a1a] hover:bg-[#333333] text-white py-3 rounded-lg font-medium tracking-wide transition"
            >
              GİRİŞ YAP
            </button>
          </form>
        </div>
      </div>
    );
  }

  const selectedChat = chats.find(c => c.session_id === selectedId);

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b px-3 sm:px-4 py-2 sm:py-3 flex-shrink-0">
        <div className="flex items-center justify-between gap-2">
          <div className="font-serif font-bold text-base sm:text-lg text-[#2c1810] whitespace-nowrap">🌸 Yönetim</div>
          <div className="flex items-center gap-1.5 flex-1 justify-center">
            <button
              onClick={() => setTab('chats')}
              data-testid="tab-chats"
              className={`px-2.5 sm:px-3 py-1.5 text-xs sm:text-sm rounded-lg transition ${tab === 'chats' ? 'bg-[#1a1a1a] text-white' : 'text-gray-600 hover:bg-gray-100'}`}
            >
              💬 Sohbet
            </button>
            <button
              onClick={() => setTab('products')}
              data-testid="tab-products"
              className={`px-2.5 sm:px-3 py-1.5 text-xs sm:text-sm rounded-lg transition ${tab === 'products' ? 'bg-[#1a1a1a] text-white' : 'text-gray-600 hover:bg-gray-100'}`}
            >
              📦 Ürünler
            </button>
          </div>
          <button
            onClick={logout}
            data-testid="admin-logout-button"
            className="flex items-center gap-1 text-xs sm:text-sm text-gray-600 hover:text-red-600 transition"
          >
            <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Çıkış</span>
          </button>
        </div>
        <div className="hidden md:flex items-center gap-3 text-xs mt-2">
          <div className="flex items-center gap-1.5 px-3 py-1 bg-green-50 text-green-700 rounded-full">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
            <Users className="w-3.5 h-3.5" /> {activeCount.active} aktif
          </div>
          <div className="flex items-center gap-1.5 px-3 py-1 bg-blue-50 text-blue-700 rounded-full">
            <MessageSquare className="w-3.5 h-3.5" /> {chats.length} sohbet
          </div>
        </div>
      </div>

      {/* Body */}
      {tab === 'products' ? (
        <ProductsTab
          products={products}
          reload={reloadProducts}
          onDelete={deleteProduct}
          token={token}
          showAdd={showAddProduct}
          setShowAdd={setShowAddProduct}
        />
      ) : (
        <div className="flex-1 flex overflow-hidden">
        {/* Chat List Sidebar */}
        <div className="w-full md:w-80 bg-white border-r overflow-y-auto flex-shrink-0" style={{ display: selectedId && window.innerWidth < 768 ? 'none' : 'block' }}>
          <div className="p-3 border-b bg-[#faf7f2] font-semibold text-sm text-[#2c1810] sticky top-0">
            Sohbetler ({chats.length})
          </div>
          {chats.length === 0 ? (
            <div className="p-8 text-center text-gray-400 text-sm">
              Henüz sohbet yok
            </div>
          ) : (
            chats.map(c => (
              <button
                key={c.session_id}
                onClick={() => setSelectedId(c.session_id)}
                data-testid={`chat-item-${c.session_id}`}
                className={`w-full text-left px-4 py-3 border-b hover:bg-gray-50 transition ${
                  selectedId === c.session_id ? 'bg-[#faf7f2]' : ''
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <div className="font-semibold text-sm text-[#2c1810] truncate">
                    {c.name} {c.surname}
                  </div>
                  {c.unread_by_admin > 0 && (
                    <span className="bg-red-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
                      {c.unread_by_admin}
                    </span>
                  )}
                </div>
                <div className="text-xs text-gray-500 truncate">📞 {c.phone}</div>
                <div className="text-[10px] text-gray-400 mt-1">
                  {new Date(c.last_message_at).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                </div>
              </button>
            ))
          )}
        </div>

        {/* Chat View */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {!selectedChat ? (
            <div className="flex-1 flex items-center justify-center text-gray-400">
              <div className="text-center">
                <MessageSquare className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p>Bir sohbet seçin</p>
              </div>
            </div>
          ) : (
            <>
              {/* Chat Header */}
              <div className="bg-white border-b p-3 flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-3">
                  <button onClick={() => setSelectedId(null)} className="md:hidden text-gray-600">←</button>
                  <div>
                    <div className="font-semibold text-[#2c1810]">{selectedChat.name} {selectedChat.surname}</div>
                    <div className="text-xs text-gray-500">📞 {selectedChat.phone}</div>
                  </div>
                </div>
                <button
                  onClick={() => deleteChat(selectedId)}
                  data-testid="delete-chat-button"
                  className="flex items-center gap-1 text-red-500 hover:bg-red-50 px-3 py-1.5 rounded-lg text-sm transition"
                >
                  <Trash2 className="w-4 h-4" /> Sohbeti Sil
                </button>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#faf7f2]">
                {messages.map((m, idx) => (
                  <MessageBubble key={idx} message={m} />
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Product Picker */}
              {showProducts && (
                <div className="bg-white border-t max-h-52 overflow-y-auto p-2">
                  <div className="text-xs font-semibold text-gray-500 px-2 py-1">Ürün Seç:</div>
                  {products.slice(0, 30).map(p => (
                    <button
                      key={p.id}
                      onClick={() => sendProductLink(p)}
                      data-testid={`product-quick-${p.id}`}
                      className="w-full flex items-center gap-3 p-2 hover:bg-gray-50 rounded text-left text-sm"
                    >
                      <img src={p.images[0]} alt="" className="w-10 h-12 object-cover rounded" />
                      <div className="flex-1 min-w-0">
                        <div className="truncate text-[#2c1810]">{p.name} - {p.color}</div>
                        <div className="text-xs text-gray-500">{p.price} TL</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Reply Form */}
              <form onSubmit={sendReply} className="bg-white border-t p-3">
                <div className="flex gap-2 mb-2">
                  <button
                    type="button"
                    onClick={() => setShowProducts(!showProducts)}
                    data-testid="show-products-button"
                    className="flex items-center gap-1 px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded-full transition"
                  >
                    <Package className="w-3 h-3" /> Ürün Gönder
                  </button>
                  <button
                    type="button"
                    onClick={sendCartLink}
                    data-testid="send-cart-link-button"
                    className="flex items-center gap-1 px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded-full transition"
                  >
                    🛒 Sepete Yönlendir
                  </button>
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={reply}
                    onChange={e => setReply(e.target.value)}
                    placeholder="Cevabınızı yazın..."
                    data-testid="admin-reply-input"
                    className="flex-1 border border-gray-200 px-3 py-2 rounded-full text-sm focus:border-[#1a1a1a] focus:outline-none"
                  />
                  <button
                    type="submit"
                    disabled={!reply.trim() || sending}
                    data-testid="admin-reply-send"
                    className="w-10 h-10 bg-[#1a1a1a] hover:bg-[#333333] disabled:opacity-40 text-white rounded-full flex items-center justify-center transition"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
      )}
    </div>
  );
};

// ================= Products Tab =================
const ProductsTab = ({ products, reload, onDelete, token, showAdd, setShowAdd }) => {
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="flex-1 overflow-y-auto p-3 md:p-6 bg-gray-50">
      <div className="flex items-center justify-between gap-2 mb-4">
        <h2 className="text-base sm:text-xl font-serif font-bold text-[#2c1810] truncate">📦 Ürünler ({products.length})</h2>
        <button
          onClick={() => setShowAdd(true)}
          data-testid="add-product-button"
          className="flex items-center gap-1.5 bg-[#1a1a1a] hover:bg-[#333333] text-white px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition whitespace-nowrap flex-shrink-0"
        >
          <Plus className="w-4 h-4" /> <span className="hidden xs:inline sm:inline">Yeni Ürün</span><span className="inline xs:hidden sm:hidden">Ekle</span>
        </button>
      </div>

      {showAdd && (
        <AddProductModal
          token={token}
          onClose={() => setShowAdd(false)}
          onAdded={() => { setShowAdd(false); reload(); }}
        />
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
        {products.map(p => (
          <div key={p.id} data-testid={`admin-product-${p.id}`} className="bg-white border rounded-lg overflow-hidden shadow-sm hover:shadow-md transition">
            <div className="aspect-[3/4] bg-gray-100 relative">
              {p.images?.[0] && <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover" />}
              {p.isNew && <span className="absolute top-2 left-2 bg-red-500 text-white text-[10px] px-2 py-0.5 rounded">YENİ</span>}
            </div>
            <div className="p-2">
              <h3 className="text-xs font-medium text-[#2c1810] line-clamp-2 mb-1">{p.name}</h3>
              <p className="text-[10px] text-gray-500 mb-1">{p.category} • {p.color || 'Renk yok'}</p>
              <div className="flex items-center justify-between">
                <span className="font-bold text-sm text-[#1a1a1a]">{p.price} TL</span>
                <button
                  onClick={() => onDelete(p.id)}
                  data-testid={`delete-product-${p.id}`}
                  className="text-red-500 hover:bg-red-50 p-1 rounded transition"
                  title="Sil"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ================= Add Product Modal =================
const AddProductModal = ({ token, onClose, onAdded }) => {
  const [form, setForm] = useState({
    name: '', description: '', price: '', oldPrice: '',
    category: 'elbise', color: '', sizes: '36,38,40,42,44',
    stock: 10, isNew: true, isFeatured: true,
  });
  const [images, setImages] = useState([]); // array of base64 data URLs
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const fileInputRef = useRef();

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList);
    const newImages = [];
    for (const file of files) {
      if (file.size > 3 * 1024 * 1024) {
        setErr(`${file.name} çok büyük (max 3MB)`);
        continue;
      }
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      newImages.push(dataUrl);
    }
    setImages(prev => [...prev, ...newImages]);
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!form.name || !form.price || images.length === 0) {
      setErr('İsim, fiyat ve en az 1 fotoğraf zorunlu.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        description: form.description || `${form.name} - Kaliteli tesettür ürünü`,
        price: parseFloat(form.price),
        oldPrice: form.oldPrice ? parseFloat(form.oldPrice) : parseFloat(form.price) * 2,
        category: form.category,
        color: form.color,
        sizes: form.sizes.split(',').map(s => s.trim()).filter(Boolean),
        images: images,
        stock: parseInt(form.stock) || 10,
        isNew: !!form.isNew,
        isFeatured: !!form.isFeatured,
      };
      await axios.post(`${API}/admin/products`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      onAdded();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Eklenemedi');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b px-5 py-3 flex items-center justify-between">
          <h3 className="font-serif font-bold text-lg text-[#2c1810]">Yeni Ürün Ekle</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3">
          {err && <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">{err}</div>}

          {/* Image Upload */}
          <div>
            <label className="block text-sm font-medium mb-2">Fotoğraflar * (max 3MB / adet)</label>
            <div
              className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-[#1a1a1a] transition"
              onClick={() => fileInputRef.current?.click()}
              onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
              onDragOver={(e) => e.preventDefault()}
            >
              <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
              <p className="text-sm text-gray-600">Dosya seçin veya sürükleyip bırakın</p>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*"
                onChange={(e) => handleFiles(e.target.files)}
                data-testid="product-image-input"
                className="hidden"
              />
            </div>
            {images.length > 0 && (
              <div className="grid grid-cols-4 gap-2 mt-3">
                {images.map((img, i) => (
                  <div key={i} className="relative aspect-square rounded overflow-hidden bg-gray-100 group">
                    <img src={img} alt="" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => setImages(prev => prev.filter((_, idx) => idx !== i))}
                      className="absolute top-1 right-1 bg-black/70 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-600">Ürün Adı *</label>
              <input required data-testid="product-form-name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full border rounded px-2 py-1.5 text-sm" placeholder="Pimenta Elbise Bordo" />
            </div>
            <div>
              <label className="text-xs text-gray-600">Kategori *</label>
              <select value={form.category} onChange={e => setForm({...form, category: e.target.value})} className="w-full border rounded px-2 py-1.5 text-sm">
                <option value="elbise">Elbise</option>
                <option value="takim">Takım</option>
                <option value="alt-giyim">Alt Giyim</option>
                <option value="ust-giyim">Üst Giyim</option>
                <option value="dis-giyim">Dış Giyim</option>
                <option value="abiye">Abiye</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-600">Fiyat (TL) *</label>
              <input required type="number" data-testid="product-form-price" value={form.price} onChange={e => setForm({...form, price: e.target.value})} className="w-full border rounded px-2 py-1.5 text-sm" placeholder="1200" />
            </div>
            <div>
              <label className="text-xs text-gray-600">Eski Fiyat (opsiyonel)</label>
              <input type="number" value={form.oldPrice} onChange={e => setForm({...form, oldPrice: e.target.value})} className="w-full border rounded px-2 py-1.5 text-sm" placeholder="Otomatik = 2x" />
            </div>
            <div>
              <label className="text-xs text-gray-600">Renk</label>
              <input value={form.color} onChange={e => setForm({...form, color: e.target.value})} className="w-full border rounded px-2 py-1.5 text-sm" placeholder="Bordo" />
            </div>
            <div>
              <label className="text-xs text-gray-600">Bedenler (virgülle)</label>
              <input value={form.sizes} onChange={e => setForm({...form, sizes: e.target.value})} className="w-full border rounded px-2 py-1.5 text-sm" placeholder="36,38,40,42" />
            </div>
            <div>
              <label className="text-xs text-gray-600">Stok</label>
              <input type="number" value={form.stock} onChange={e => setForm({...form, stock: e.target.value})} className="w-full border rounded px-2 py-1.5 text-sm" />
            </div>
            <div className="flex gap-3 items-end pb-1">
              <label className="text-xs flex items-center gap-1.5">
                <input type="checkbox" checked={form.isNew} onChange={e => setForm({...form, isNew: e.target.checked})} className="accent-[#1a1a1a]" />
                Yeni Ürün
              </label>
              <label className="text-xs flex items-center gap-1.5">
                <input type="checkbox" checked={form.isFeatured} onChange={e => setForm({...form, isFeatured: e.target.checked})} className="accent-[#1a1a1a]" />
                Öne Çıkan
              </label>
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-600">Açıklama</label>
            <textarea rows={2} value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="w-full border rounded px-2 py-1.5 text-sm" placeholder="Ürün açıklaması (boş bırakılırsa otomatik)" />
          </div>

          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border border-gray-300 py-2 rounded font-medium text-sm hover:bg-gray-50">İptal</button>
            <button
              type="submit"
              disabled={saving}
              data-testid="product-form-submit"
              className="flex-1 bg-[#1a1a1a] hover:bg-[#333333] text-white py-2 rounded font-medium text-sm disabled:opacity-50"
            >
              {saving ? 'Ekleniyor...' : 'Ürünü Ekle'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const MessageBubble = ({ message }) => {
  const isAdmin = message.from === 'admin';
  const time = message.created_at ? new Date(message.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }) : '';
  return (
    <div className={`flex ${isAdmin ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[70%] ${isAdmin ? 'flex flex-col items-end' : ''}`}>
        <div className={`px-3 py-2 rounded-2xl text-sm break-words whitespace-pre-wrap ${
          isAdmin
            ? 'bg-[#1a1a1a] text-white rounded-tr-sm'
            : 'bg-white border border-gray-200 text-[#2c1810] rounded-tl-sm'
        }`}>
          {message.text}
        </div>
        <div className="text-[10px] text-gray-400 mt-1 px-1">{time}</div>
      </div>
    </div>
  );
};

export default AdminPanel;
